import React, { useState } from "react";
import { Button } from "@/components/ui/Button";
import { distinctLoginRoles, workspacesForRole } from "./loginCode";
import type { LoginWorkspace } from "./loginCode";
import { LOGIN_BUTTON, LOGIN_ERROR, LOGIN_PICK_ROW, LOGIN_PICK_HINT, LOGIN_TEXT } from "./loginStyles";

/** Italian copy for the role step (DD-026). */
const ROLE_PICK_LABELS: Record<string, { title: string; hint: string }> = {
  client: { title: "Cliente", hint: "Rivedi e approva i contenuti" },
  creator: { title: "Creator", hint: "Brief e produzione contenuti" },
  editor: { title: "Editor", hint: "Montaggio e post-produzione" },
};

interface WorkspacePickFunnelProps {
  workspaces: LoginWorkspace[];
  /** Called with the chosen workspace's clientId. */
  onPick: (clientId: string) => void;
  disabled?: boolean;
  error?: string | null;
  /** Extra actions below the list (e.g. "request a new code"). */
  footer?: React.ReactNode;
}

/**
 * The DD-026 login funnel: when the workspaces span more than one role,
 * ask "which role" first, then show the role-filtered client list. A
 * single workspace under the chosen role skips the list. Shared by the
 * code login and the emailed-link login — the email never decides.
 */
export const WorkspacePickFunnel: React.FC<WorkspacePickFunnelProps> = ({
  workspaces,
  onPick,
  disabled = false,
  error = null,
  footer,
}) => {
  const roles = distinctLoginRoles(workspaces);
  const [chosenRole, setChosenRole] = useState<string | null>(null);

  const errorBox = error ? <p className={LOGIN_ERROR}>{error}</p> : null;

  const pickRole = (role: string) => {
    const scoped = workspacesForRole(workspaces, role);
    if (scoped.length === 1) {
      onPick(scoped[0].clientId);
      return;
    }
    setChosenRole(role);
  };

  if (roles.length > 1 && !chosenRole) {
    return (
      <div className="space-y-[12px]">
        <p className={LOGIN_TEXT}>Come vuoi accedere?</p>
        {roles.map((role) => {
          const copy = ROLE_PICK_LABELS[role] || ROLE_PICK_LABELS.client;
          return (
            <button
              key={role}
              type="button"
              disabled={disabled}
              onClick={() => pickRole(role)}
              className={LOGIN_PICK_ROW}
            >
              <span className="block font-medium text-gray-900">{copy.title}</span>
              <span className={LOGIN_PICK_HINT}>{copy.hint}</span>
            </button>
          );
        })}
        {errorBox}
      </div>
    );
  }

  const pickable = chosenRole
    ? workspacesForRole(workspaces, chosenRole)
    : workspaces;

  return (
    <div className="space-y-[12px]">
      <p className={LOGIN_TEXT}>Scegli il workspace:</p>
      {pickable.map((c) => (
        <button
          key={c.clientId}
          type="button"
          disabled={disabled}
          onClick={() => onPick(c.clientId)}
          className={LOGIN_PICK_ROW}
        >
          {c.clientName}
        </button>
      ))}
      {errorBox}
      {chosenRole && (
        <Button type="button" variant="secondary" size="lg" className={LOGIN_BUTTON} onClick={() => setChosenRole(null)}>
          Torna alla scelta del ruolo
        </Button>
      )}
      {footer}
    </div>
  );
};
